import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  FlexCol,
  FlexRow,
  FormSelect,
  NoraButton,
  Textarea,
} from '../../../../common/components/v2';
import { Decision } from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { DecisionUpdateReasons } from '../decisionUpdateReasons';
import styles from '../styles.module.scss';
import useWaiveForm, { WaiveFormData } from './useWaiveForm';

const decisionOptions = [
  { value: Decision.DECLINED, label: Resource.DECLINE_OPTION },
  { value: Decision.APPROVED, label: Resource.APPROVE_OPTION },
  { value: Decision.POSTPONED, label: Resource.POSTPONE_OPTION },
  { value: Decision.INELIGIBLE, label: Resource.INELIGIBLE_OPTION },
  { value: Decision.WAIVED, label: Resource.WAIVED_OPTION },
];

const decisionUpdateOptions = Object.values(DecisionUpdateReasons).map(
  value => ({ value, label: value }),
);

const WaiveForm = ({
  isUpdate,
  isOpen,
  onSuccess,
  initialFormData,
  onDecisionChanged,
}: {
  isUpdate: boolean;
  isOpen: boolean;
  onSuccess: (formData: WaiveFormData) => void;
  initialFormData?: WaiveFormData | null;
  onDecisionChanged?: (decision: Decision) => void;
}) => {
  const { handleSubmit, control, clear, reset, watch } = useWaiveForm({
    isUpdate,
  });
  const decision = watch('decision');

  useEffect(() => {
    if (isOpen) return;
    clear();
  }, [isOpen, clear]);

  useEffect(() => {
    if (!initialFormData) return;
    reset(initialFormData);
  }, [reset, initialFormData]);

  useEffect(() => {
    onDecisionChanged?.(decision);
  }, [onDecisionChanged, decision]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box flex={1} display="flex" flexDirection="column" gap={3}>
        {isUpdate && (
          <>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="updateReason"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Reason for update"
                      options={decisionUpdateOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
            <FlexRow>
              <FlexCol xs={12}>
                <Controller
                  control={control}
                  name="decision"
                  render={({ field, fieldState }) => (
                    <FormSelect
                      required
                      label="Decision"
                      options={decisionOptions}
                      error={fieldState.error?.message}
                      {...field}
                    />
                  )}
                />
              </FlexCol>
            </FlexRow>
          </>
        )}
        <FlexRow>
          <FlexCol xs={12}>
            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => (
                <Textarea
                  required
                  label="Description"
                  error={fieldState.error?.message}
                  {...field}
                />
              )}
            />
          </FlexCol>
        </FlexRow>
      </Box>
      <NoraButton
        className={styles.DrawerButton}
        variant="primary"
        name={ElementLocators.LOCATOR_REVIEW_WAIVE_BUTTON}
        onClick={handleSubmit(onSuccess)}
      >
        {isUpdate ? Resource.REVIEW_DECISION : Resource.REVIEW_APPLICATION}
      </NoraButton>
    </Box>
  );
};

export default WaiveForm;
